import React from "react";

import i18n from "@/i18n";
import { useGetProfileQuery } from "@/queries/hooks/profileHooks";
import type { Profile } from "@/schemas";

type ProfileContextValue = {
  profile: Profile | null;
  setProfile: React.Dispatch<React.SetStateAction<Profile | null>>;
  isLoading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
};

const ProfileContext = React.createContext<ProfileContextValue | undefined>(
  undefined,
);

export function useProfile() {
  const ctx = React.useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
}

type ProfileProviderProps = {
  children: React.ReactNode;
};

export function ProfileProvider({ children }: ProfileProviderProps) {
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const profileQuery = useGetProfileQuery();

  // Sync profile state when query data becomes available
  React.useEffect(() => {
    if (profileQuery.data) {
      setProfile(profileQuery.data.profile);
    }
  }, [profileQuery.data]);

  const refreshProfile = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await profileQuery.refetch();
      if (res.data) setProfile(res.data.profile);
    } catch (e: any) {
      setError(
        e?.message ?? i18n.t("profileContext.loadFailed", { ns: "onboarding" }),
      );
    } finally {
      setIsLoading(false);
    }
  }, [profileQuery]);

  const value: ProfileContextValue = React.useMemo(
    () => ({ profile, setProfile, isLoading, error, refreshProfile }),
    [profile, isLoading, error, refreshProfile],
  );

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}
